import React, { useState, useRef, useCallback } from 'react';
import { 
  ArrowLeft, 
  Copy, 
  ExternalLink, 
  Building2, 
  Package, 
  QrCode, 
  Eye,
  Search,
  Settings,
  ChevronUp,
  Star,
  Share2,
  Check,
  MessageCircleMore,
  HardHat,
  Download
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useBusiness } from '../../../../contexts/BusinessContext';
import { useLots } from '../../../../contexts/LotsContext';
import { Switch } from '@headlessui/react';
import QRCode from 'react-qr-code';
import html2canvas from 'html2canvas';
import TiboLogo from '../../../../components/assets/tibo-logo.svg';
import { BusinessHeader } from '../../../../components/business/BusinessHeader';

export function CatalogScreen() {
  const navigate = useNavigate();
  const { business } = useBusiness();
  const { lots, updateLot, catalogStatus, updateCatalogStatus } = useLots();
  
  // Estados existentes (removemos los que ahora vienen del contexto)
  const [showQRModal, setShowQRModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSettings, setShowSettings] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showConstructionModal, setShowConstructionModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [qrModalAnimation, setQrModalAnimation] = useState(false);
  const [shareModalAnimation, setShareModalAnimation] = useState(false);
  
  // Desestructuramos el catalogStatus
  const { isOnline, showPrices, showQuantities } = catalogStatus;

  // Estados para infinite scroll
  const observer = useRef();
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  // Transformar lotes a productos con featured
  const products = lots
    .filter(lot => lot.quantity > 0)
    .map(lot => ({
      ...lot,
      visible: lot.is_public,
      featured: lot.is_featured // Asumimos que esto vendrá del backend después
    }));

  // Filtrar productos por búsqueda
  const filteredProducts = products.filter(product => 
    product.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Ordenar productos (featured primero)
  const sortedProducts = [...filteredProducts].sort((a, b) => {
    if (a.featured && !b.featured) return -1;
    if (!a.featured && b.featured) return 1;
    return 0;
  });

  // Lógica de infinite scroll
  const lastProductRef = useCallback(node => {
    if (loading) return;
    if (observer.current) observer.current.disconnect();
    
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && sortedProducts.length >= page * 10) {
        setPage(prev => prev + 1);
      }
    });

    if (node) observer.current.observe(node);
  }, [loading, sortedProducts.length, page]);

  // Productos paginados
  const displayedProducts = sortedProducts.slice(0, page * 10);

  // URL para compartir el catálogo (usando solo ID para máxima performance)
  const catalogUrl = `${window.location.origin}/c/${business?.id}`;

  // Filtrar solo los lotes con cantidad > 0
  const availableLots = lots.filter(lot => lot.quantity > 0);
  const visibleLots = availableLots.filter(lot => lot.is_public);

  const handleToggleLotVisibility = async (lotId, newValue) => {
    try {
      await updateLot(lotId, { is_public: newValue });
    } catch (error) {
      console.error('Error updating lot visibility:', error);
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(catalogUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handlePreview = () => {
    window.open(catalogUrl, '_blank');
  };

  // Handlers para los toggles
  const handleToggleOnline = (value) => {
    updateCatalogStatus({ isOnline: value });
  };

  const handleTogglePrices = (value) => {
    updateCatalogStatus({ showPrices: value });
  };

  const handleToggleQuantities = (value) => {
    updateCatalogStatus({ showQuantities: value });
  };

  // Funciones para animación suave
  const openQRModal = () => {
    setShowQRModal(true);
    // Pequeño retraso para activar la animación después de montar el componente
    setTimeout(() => setQrModalAnimation(true), 10);
  };

  const closeQRModal = () => {
    setQrModalAnimation(false);
    // Esperar a que termine la animación antes de desmontar el componente
    setTimeout(() => setShowQRModal(false), 300);
  };

  const openShareModal = () => {
    setShowShareModal(true);
    // Pequeño retraso para activar la animación después de montar el componente
    setTimeout(() => setShareModalAnimation(true), 10);
  };

  const closeShareModal = () => {
    setShareModalAnimation(false);
    // Esperar a que termine la animación antes de desmontar el componente
    setTimeout(() => setShowShareModal(false), 300);
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header Principal */}
      <div className="sticky top-0 z-20 bg-white dark:bg-gray-800 border-b dark:border-gray-700">
        <div className="flex items-center justify-between p-4">
          <div className="flex items-center gap-3">
            <button 
              onClick={() => navigate('/')}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-colors"
            >
              <ArrowLeft className="w-5 h-5 dark:text-gray-200" />
            </button>
            <h1 className="text-xl font-semibold dark:text-white">Catálogo Digital</h1>
          </div>
          <div className="flex items-center gap-2">
            <button 
              onClick={handlePreview}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-colors"
              title="Vista previa"
            >
              <Eye className="w-5 h-5 text-gray-600 dark:text-gray-300" />
            </button>
            <button 
              onClick={() => setShowSettings(true)}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-colors"
              title="Configuración"
            >
              <Settings className="w-5 h-5 text-gray-600 dark:text-gray-300" />
            </button>
          </div>
        </div>
      </div>

      {/* Business Header */}
      <BusinessHeader 
        business={business}
        isOnline={isOnline}
        onToggleOnline={handleToggleOnline}
        className="border-b dark:border-gray-700"
      />

      {/* Buscador */}
      <div className="bg-white dark:bg-gray-800 border-b dark:border-gray-700 px-4 py-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Buscar productos..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-3 bg-gray-50 dark:bg-gray-700 rounded-xl text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      {/* Content */}
      <div className="p-4 pb-24 space-y-4">
        {/* Contador de productos visibles */}
        <div className="text-sm text-gray-500">
          {displayedProducts.filter(p => p.visible).length} productos visibles
        </div>

        {/* Lista de Productos */}
        <div className="space-y-3">
          {displayedProducts.map((product, index) => (
            <div 
              key={product.id}
              ref={index === displayedProducts.length - 1 ? lastProductRef : null}
              className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center flex-shrink-0">
                  {product.image ? (
                    <img 
                      src={product.image}
                      alt={product.title}
                      className="w-full h-full object-cover rounded-lg"
                    />
                  ) : (
                    <Package className="w-6 h-6 text-gray-400" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-medium truncate">{product.title}</h3>
                    {product.featured && (
                      <Star className="w-4 h-4 text-blue-500 fill-current" />
                    )}
                  </div>
                  <div className="text-sm text-gray-500 space-x-2">
                    {showQuantities && <span>{product.quantity} {product.unit}</span>}
                    {showQuantities && showPrices && <span>•</span>}
                    {showPrices && <span>${product.unitPrice?.toLocaleString()}</span>}
                  </div>
                </div>
                <Switch
                  checked={product.visible}
                  onChange={(newValue) => handleToggleLotVisibility(product.id, newValue)}
                  className={`${product.visible ? 'bg-[#00594C]' : 'bg-gray-200'} relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#00594C] focus:ring-offset-2`}
                >
                  <span className="sr-only">Activar producto</span>
                  <span
                    className={`${product.visible ? 'translate-x-6' : 'translate-x-1'} inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                  />
                </Switch>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Action Bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-800 border-t dark:border-gray-700 p-4">
        <div className="max-w-lg mx-auto">
          <button
            onClick={openShareModal}
            className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-[#00594C] hover:bg-[#004C41] text-white rounded-xl font-medium transition-colors"
          >
            <Share2 className="w-5 h-5" />
            <span>Compartir</span>
          </button>
        </div>
      </div>

      {/* Toast de confirmación */}
      <div className={`fixed bottom-32 left-1/2 -translate-x-1/2 bg-gray-800 text-white px-4 py-2 rounded-lg transition-all duration-200 ${copied ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2 pointer-events-none'}`}>
        Link copiado al portapapeles
      </div>

      {/* Modal de Compartir */}
      {showShareModal && (
        <div 
          className="fixed inset-0 bg-black/50 z-50 transition-opacity duration-300 ease-in-out"
          style={{ opacity: shareModalAnimation ? '1' : '0' }}
          onClick={closeShareModal}
        >
          <div 
            className="absolute bottom-0 left-0 right-0 bg-white dark:bg-gray-800 rounded-t-3xl transform transition-transform duration-300 ease-out w-full max-w-md mx-auto"
            style={{ 
              transform: shareModalAnimation ? 'translateY(0)' : 'translateY(100%)',
              transitionTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)' 
            }}
            onClick={e => e.stopPropagation()}
          >
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-medium text-gray-900 dark:text-white">Compartir catálogo</h3>
                <button 
                  onClick={closeShareModal}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full"
                >
                  <ChevronUp className="w-6 h-6 text-gray-500" />
                </button>
              </div>
              
              <div className="grid grid-cols-3 gap-3">
                <button
                  onClick={() => {
                    openQRModal();
                    closeShareModal();
                  }}
                  className="flex flex-col items-center justify-center gap-2 px-2 py-4 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-xl transition-colors"
                >
                  <QrCode className="w-8 h-8" />
                  <span className="text-sm">Ver QR</span>
                </button>
                
                <button
                  onClick={handleCopyLink}
                  className="flex flex-col items-center justify-center gap-2 px-2 py-4 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-xl transition-colors"
                >
                  {copied ? (
                    <>
                      <Check className="w-8 h-8 text-green-500" />
                      <span className="text-sm">¡Copiado!</span>
                    </>
                  ) : (
                    <>
                      <Copy className="w-8 h-8" />
                      <span className="text-sm">Copiar link</span>
                    </>
                  )}
                </button>
                
                <button
                  onClick={() => {
                    // Usar whatsapp:// en lugar de https://wa.me para abrir WhatsApp estándar
                    const message = encodeURIComponent(`Hola, te comparto el catálogo de ${business?.name || 'mi negocio'}: ${catalogUrl}`);
                    const whatsappUrl = `whatsapp://send?text=${message}`;
                    
                    // Intentar abrir WhatsApp estándar
                    window.location.href = whatsappUrl;
                    
                    // Fallback a wa.me si whatsapp:// no funciona (después de un breve retraso)
                    setTimeout(() => {
                      window.open(`https://wa.me/?text=${message}`, '_blank');
                    }, 300);
                    
                    closeShareModal();
                  }}
                  className="flex flex-col items-center justify-center gap-2 px-2 py-4 bg-[#25D366] hover:bg-[#20BD5C] text-white rounded-xl transition-colors"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-8 h-8">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                  </svg>
                  <span className="text-sm">WhatsApp</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Settings Modal */}
      {showSettings && (
        <div 
          className="fixed inset-0 bg-black/50 z-50"
          onClick={() => setShowSettings(false)}
        >
          <div 
            className="absolute bottom-0 left-0 right-0 bg-white dark:bg-gray-800 rounded-t-3xl"
            onClick={e => e.stopPropagation()}
          >
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold dark:text-white">Configuración del catálogo</h2>
                <button 
                  onClick={() => setShowSettings(false)}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full"
                >
                  <ChevronUp className="w-6 h-6 text-gray-500" />
                </button>
              </div>

              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-medium mb-4 dark:text-white">Estado del Catálogo</h3>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-medium dark:text-white">Catálogo en línea</h4>
                        <p className="text-sm text-gray-500">Habilitar vista pública del catálogo</p>
                      </div>
                      <Switch
                        checked={isOnline}
                        onChange={handleToggleOnline}
                        className={`${isOnline ? 'bg-[#00594C]' : 'bg-gray-200'} relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#00594C] focus:ring-offset-2`}
                      >
                        <span className="sr-only">Activar catálogo</span>
                        <span
                          className={`${isOnline ? 'translate-x-6' : 'translate-x-1'} inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                        />
                      </Switch>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-medium mb-4 dark:text-white">Visibilidad</h3>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-medium dark:text-white">Mostrar precios</h4>
                        <p className="text-sm text-gray-500">Los clientes verán los precios</p>
                      </div>
                      <Switch
                        checked={showPrices}
                        onChange={handleTogglePrices}
                        className={`${showPrices ? 'bg-[#00594C]' : 'bg-gray-200'} relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#00594C] focus:ring-offset-2`}
                      >
                        <span className="sr-only">Mostrar precios</span>
                        <span
                          className={`${showPrices ? 'translate-x-6' : 'translate-x-1'} inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                        />
                      </Switch>
                    </div>

                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-medium dark:text-white">Mostrar cantidades</h4>
                        <p className="text-sm text-gray-500">Los clientes verán el stock disponible</p>
                      </div>
                      <Switch
                        checked={showQuantities}
                        onChange={handleToggleQuantities}
                        className={`${showQuantities ? 'bg-[#00594C]' : 'bg-gray-200'} relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#00594C] focus:ring-offset-2`}
                      >
                        <span className="sr-only">Mostrar cantidades</span>
                        <span
                          className={`${showQuantities ? 'translate-x-6' : 'translate-x-1'} inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                        />
                      </Switch>
                    </div>
                  </div>
                </div>

                <div className="mt-8">
                  <h3 className="text-lg font-medium mb-4 dark:text-white">Diagnóstico</h3>
                  <button
                    onClick={async () => {
                      try {
                        console.log('Iniciando diagnóstico de visibilidad de lotes...');
                        
                        // Verificar lotes actuales
                        const filteredLots = lots.filter(lot => lot.quantity > 0);
                        console.log('Lotes con cantidad > 0:', filteredLots.length);
                        
                        const publicLots = filteredLots.filter(lot => lot.is_public);
                        console.log('Lotes públicos con cantidad > 0:', publicLots.length);
                        
                        // Intentar hacer públicos todos los lotes con cantidad
                        if (filteredLots.length > 0 && publicLots.length === 0) {
                          console.log('No hay lotes públicos. Marcando todos los lotes como públicos...');
                          
                          for (const lot of filteredLots) {
                            console.log(`Marcando lote ${lot.id} (${lot.title}) como público...`);
                            await updateLot(lot.id, { is_public: true });
                          }
                          
                          console.log('Proceso completado. Por favor, verifique el catálogo público.');
                          alert(`Se han marcado ${filteredLots.length} lotes como públicos. Verifique el catálogo ahora.`);
                        } else if (publicLots.length > 0) {
                          console.log('Ya hay lotes públicos disponibles.');
                          alert(`Ya hay ${publicLots.length} lotes marcados como públicos.`);
                        } else {
                          console.log('No hay lotes con cantidad > 0.');
                          alert('No hay lotes con cantidad disponible para mostrar en el catálogo.');
                        }
                      } catch (error) {
                        console.error('Error en diagnóstico:', error);
                        alert('Error al realizar el diagnóstico. Consulte la consola para más detalles.');
                      }
                    }}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-yellow-500 hover:bg-yellow-600 text-white rounded-xl font-medium transition-colors"
                  >
                    Verificar y corregir visibilidad de lotes
                  </button>
                  <p className="text-xs text-gray-500 mt-2 text-center">
                    Este botón verifica y corrige la visibilidad de los lotes en el catálogo público.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* QR Modal como Bottom Sheet */}
      {showQRModal && (
        <div 
          className="fixed inset-0 bg-black/50 z-50 transition-opacity duration-300 ease-in-out"
          style={{ opacity: qrModalAnimation ? '1' : '0' }}
          onClick={closeQRModal}
        >
          <div 
            className="absolute bottom-0 left-0 right-0 bg-white dark:bg-gray-800 rounded-t-3xl transform transition-transform duration-300 ease-out"
            style={{ 
              transform: qrModalAnimation ? 'translateY(0)' : 'translateY(100%)',
              transitionTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)' 
            }}
            onClick={e => e.stopPropagation()}
          >
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-medium text-gray-900 dark:text-white">Código QR del catálogo</h3>
                <button 
                  onClick={closeQRModal}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full"
                >
                  <ChevronUp className="w-6 h-6 text-gray-500" />
                </button>
              </div>
              
              <div id="qr-container" className="bg-white p-4 rounded-lg flex flex-col items-center justify-center gap-4 max-w-md mx-auto">
                {/* Logo de Tibó */}
                <div className="flex items-center justify-center">
                  <img src={TiboLogo} alt="Tibó" className="h-12 mb-2" />
                </div>

                <div className="text-center mt-2 mb-3">
                  <h2 className="text-2xl font-bold text-gray-900">{business?.name || 'Tu Negocio'}</h2>
                </div>
                <div className="w-64 h-64 bg-white p-2 rounded-lg flex items-center justify-center">
                  <QRCode 
                    value={catalogUrl} 
                    size={230}
                    level="H"
                    fgColor="#000000" 
                    bgColor="#FFFFFF"
                  />
                </div>
                <div className="text-center mt-2">
                  <p className="text-sm text-gray-600">Escaneá este código para acceder al catálogo de <strong>{business?.name || 'Tu Negocio'}</strong></p>
                </div>
                <div className="text-center text-sm font-medium text-[#00594C] mt-2">
                  <p>Creado por Tibó</p>
                </div>
              </div>
              
              <div className="mt-8 max-w-md mx-auto">
                <button
                  onClick={() => {
                    // Crear un canvas con todo el contenido usando html2canvas
                    const qrContainer = document.getElementById('qr-container');
                    
                    html2canvas(qrContainer, {
                      backgroundColor: '#FFFFFF',
                      scale: 2, // Mayor calidad para la imagen
                      logging: false,
                      useCORS: true
                    }).then(canvas => {
                      // Convertir a imagen y descargar
                      const link = document.createElement('a');
                      link.download = `catalogo-${business?.id}.png`;
                      link.href = canvas.toDataURL('image/png');
                      link.click();
                    });
                  }}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-white hover:bg-gray-100 text-[#00594C] border-2 border-[#00594C] rounded-xl font-medium transition-colors"
                >
                  <Download className="w-5 h-5" />
                  <span>Descargar código QR</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {showPreview && (
        <div 
          className="fixed inset-0 bg-black/50 z-50"
          onClick={() => setShowPreview(false)}
        >
          <div 
            className="absolute inset-4 bg-white dark:bg-gray-800 rounded-lg overflow-hidden flex flex-col"
            onClick={e => e.stopPropagation()}
          >
            <div className="p-4 flex items-center justify-between border-b dark:border-gray-700">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">Vista previa del catálogo</h3>
              <button 
                onClick={() => setShowPreview(false)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full"
              >
                <ChevronUp className="w-6 h-6 text-gray-500" />
              </button>
            </div>
            <div className="flex-1 overflow-auto">
              <iframe 
                src={catalogUrl} 
                title="Previsualización del catálogo" 
                className="w-full h-full border-0"
              />
            </div>
            <div className="p-4 border-t dark:border-gray-700 flex justify-between">
              <button
                onClick={() => setShowPreview(false)}
                className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-white rounded-lg"
              >
                Cerrar
              </button>
              <button
                onClick={handlePreview}
                className="px-4 py-2 bg-[#00594C] hover:bg-[#004C41] text-white rounded-lg flex items-center gap-2"
              >
                <ExternalLink className="w-4 h-4" />
                Abrir en nueva pestaña
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
} 